import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PageHeader } from "@/components/layout/PageHeader";
import { StatusBadge } from "@/components/shared/StatusBadge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft,
  Calendar,
  MapPin,
  User,
  Package,
  GripVertical,
  Send,
  Trash2,
  ChevronUp,
  ChevronDown,
  Clock,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import { format } from "date-fns";

interface RouteStop {
  id: string;
  sequence: number;
  shipmentId: string;
  shipmentType: string;
  orderNo: string;
  customerName: string;
  addressLine: string;
  areaName: string;
  scheduledTimeSlot: string | null;
  estimatedArrival: string | null;
  actualArrival: string | null;
  status: string;
}

interface RouteDetail {
  id: string;
  routeNo: string;
  date: string;
  cityId: string;
  cityName: string;
  zoneId: string | null;
  zoneName: string | null;
  assignedRiderId: string | null;
  riderName: string | null;
  status: string;
  plannedStartAt: string | null;
  plannedEndAt: string | null;
  actualStartAt: string | null;
  actualEndAt: string | null;
  stops: RouteStop[];
}

interface Rider {
  id: string;
  name: string;
}

export default function RouteDetailPage() {
  const navigate = useNavigate();
  const { routeId } = useParams<{ routeId: string }>();
  const [route, setRoute] = useState<RouteDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [riders, setRiders] = useState<Rider[]>([]);
  const [publishing, setPublishing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [reordering, setReordering] = useState(false);
  const [localStops, setLocalStops] = useState<RouteStop[]>([]);

  useEffect(() => {
    if (routeId) fetchRoute();
  }, [routeId]);

  useEffect(() => {
    if (route) {
      setLocalStops(route.stops);
    }
  }, [route]);

  async function fetchRoute() {
    if (!routeId) return;
    setLoading(true);
    try {
      const res = await fetch(`/api/admin/routes/${routeId}`);
      if (!res.ok) {
        navigate("/admin/routes");
        return;
      }
      const data = await res.json();
      setRoute(data);
      fetchRiders(data.cityId);
    } catch (error) {
      console.error("Error fetching route:", error);
    } finally {
      setLoading(false);
    }
  }

  async function fetchRiders(cityId: string) {
    try {
      const res = await fetch(`/api/admin/routes?cityId=${cityId}`);
      const data = await res.json();
      const riderSet = new Map<string, Rider>();
      for (const r of data.routes || []) {
        if (r.assignedRiderId && r.riderName) {
          riderSet.set(r.assignedRiderId, { id: r.assignedRiderId, name: r.riderName });
        }
      }
      setRiders(Array.from(riderSet.values()));
    } catch (error) {
      console.error("Error fetching riders:", error);
    }
  }

  async function handlePublish() {
    setPublishing(true);
    try {
      const res = await fetch(`/api/admin/routes/${routeId}/publish`, {
        method: "POST",
      });
      if (res.ok) {
        fetchRoute();
      } else {
        const data = await res.json();
        alert(data.error || "Failed to publish route");
      }
    } catch (error) {
      console.error("Error publishing route:", error);
    } finally {
      setPublishing(false);
    }
  }

  async function handleDelete() {
    setDeleting(true);
    try {
      const res = await fetch(`/api/admin/routes/${routeId}`, {
        method: "DELETE",
      });
      if (res.ok) {
        navigate("/admin/routes");
      } else {
        const data = await res.json();
        alert(data.error || "Failed to delete route");
      }
    } catch (error) {
      console.error("Error deleting route:", error);
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
    }
  }

  async function handleStatusChange(status: string) {
    try {
      const res = await fetch(`/api/admin/routes/${routeId}/status`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ toStatus: status }),
      });
      if (res.ok) {
        fetchRoute();
      } else {
        const data = await res.json();
        alert(data.error || "Failed to update status");
      }
    } catch (error) {
      console.error("Error updating status:", error);
    }
  }

  const moveStop = useCallback((index: number, direction: "up" | "down") => {
    const newStops = [...localStops];
    const newIndex = direction === "up" ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= newStops.length) return;

    [newStops[index], newStops[newIndex]] = [newStops[newIndex], newStops[index]];
    newStops.forEach((stop, i) => {
      stop.sequence = i + 1;
    });
    setLocalStops(newStops);
  }, [localStops]);

  async function saveReorder() {
    setReordering(true);
    try {
      const res = await fetch(`/api/admin/routes/${routeId}/reorder`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          stopOrder: localStops.map(s => s.id),
        }),
      });
      if (res.ok) {
        fetchRoute();
      } else {
        const data = await res.json();
        alert(data.error || "Failed to reorder stops");
      }
    } catch (error) {
      console.error("Error reordering stops:", error);
    } finally {
      setReordering(false);
    }
  }

  const hasOrderChanged = route && JSON.stringify(localStops.map(s => s.id)) !== JSON.stringify(route.stops.map(s => s.id));

  if (loading) {
    return (
      <div>
        <div className="flex items-center gap-4 mb-6">
          <Skeleton className="h-10 w-10" />
          <div>
            <Skeleton className="h-8 w-48 mb-2" />
            <Skeleton className="h-4 w-32" />
          </div>
        </div>
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            {[1, 2, 3].map((i) => (
              <Card key={i}>
                <CardContent className="pt-6">
                  <Skeleton className="h-20 w-full" />
                </CardContent>
              </Card>
            ))}
          </div>
          <Card>
            <CardContent className="pt-6">
              <Skeleton className="h-40 w-full" />
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!route) {
    return null;
  }

  const isDraft = route.status === "DRAFT";
  const isPublished = route.status === "PUBLISHED";
  const isInProgress = route.status === "IN_PROGRESS";
  const isCompleted = route.status === "COMPLETED";

  return (
    <div>
      <PageHeader
        title={
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" asChild>
              <Link to="/admin/routes">
                <ArrowLeft className="h-5 w-5" />
              </Link>
            </Button>
            <span>{route.routeNo}</span>
            <StatusBadge status={route.status.toLowerCase() as any} />
          </div>
        }
        subtitle={`${route.cityName}${route.zoneName ? ` - ${route.zoneName}` : ""}`}
        actions={
          <div className="flex items-center gap-2">
            {isDraft && (
              <>
                <Button
                  variant="outline"
                  onClick={() => setDeleteDialogOpen(true)}
                  disabled={deleting}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </Button>
                <Button onClick={handlePublish} disabled={publishing}>
                  <Send className="h-4 w-4 mr-2" />
                  {publishing ? "Publishing..." : "Publish Route"}
                </Button>
              </>
            )}
            {isPublished && (
              <Button onClick={() => handleStatusChange("IN_PROGRESS")}>
                Start Route
              </Button>
            )}
            {isInProgress && (
              <Button onClick={() => handleStatusChange("COMPLETED")}>
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Complete Route
              </Button>
            )}
          </div>
        }
      />

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Stops ({localStops.length})</CardTitle>
                {isDraft && hasOrderChanged && (
                  <Button size="sm" onClick={saveReorder} disabled={reordering}>
                    {reordering ? "Saving..." : "Save Order"}
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {localStops.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">
                  No stops in this route
                </p>
              ) : (
                <div className="space-y-3">
                  {localStops.map((stop, index) => (
                    <div
                      key={stop.id}
                      className="flex items-start gap-3 p-4 rounded-lg border bg-card"
                    >
                      {isDraft && (
                        <div className="flex flex-col gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => moveStop(index, "up")}
                            disabled={index === 0}
                          >
                            <ChevronUp className="h-4 w-4" />
                          </Button>
                          <GripVertical className="h-4 w-4 text-muted-foreground mx-auto" />
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => moveStop(index, "down")}
                            disabled={index === localStops.length - 1}
                          >
                            <ChevronDown className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                      <div className="flex items-center justify-center h-8 w-8 rounded-full bg-primary text-primary-foreground font-semibold text-sm flex-shrink-0">
                        {stop.sequence}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-semibold">{stop.orderNo}</p>
                          <Badge variant="outline" className="text-xs">
                            {stop.shipmentType}
                          </Badge>
                          {stop.status !== "SCHEDULED" && (
                            <StatusBadge status={stop.status.toLowerCase() as any} />
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {stop.customerName}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">
                          <MapPin className="h-3 w-3 inline mr-1" />
                          {stop.addressLine}
                        </p>
                        {stop.scheduledTimeSlot && (
                          <p className="text-sm text-muted-foreground mt-1">
                            <Clock className="h-3 w-3 inline mr-1" />
                            {stop.scheduledTimeSlot}
                          </p>
                        )}
                        {stop.actualArrival && (
                          <p className="text-sm text-green-600 mt-1">
                            <CheckCircle2 className="h-3 w-3 inline mr-1" />
                            Arrived: {format(new Date(stop.actualArrival), "h:mm a")}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Route Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3">
                <Calendar className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Date</p>
                  <p className="font-medium">{format(new Date(route.date), "EEEE, MMMM d, yyyy")}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <MapPin className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Location</p>
                  <p className="font-medium">
                    {route.cityName}
                    {route.zoneName && <span className="text-muted-foreground"> / {route.zoneName}</span>}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Package className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Total Stops</p>
                  <p className="font-medium">{route.stops.length} stops</p>
                </div>
              </div>
              {route.plannedStartAt && route.plannedEndAt && (
                <div className="flex items-center gap-3">
                  <Clock className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Planned Time</p>
                    <p className="font-medium">
                      {format(new Date(route.plannedStartAt), "h:mm a")} -{" "}
                      {format(new Date(route.plannedEndAt), "h:mm a")}
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Assigned Rider</CardTitle>
            </CardHeader>
            <CardContent>
              {route.riderName ? (
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium">{route.riderName}</p>
                    <p className="text-sm text-muted-foreground">Rider</p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-4">
                  <User className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">No rider assigned</p>
                  {isDraft && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Rider will be auto-assigned on publish
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {(isInProgress || isCompleted) && (
            <Card>
              <CardHeader>
                <CardTitle>Progress</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {route.actualStartAt && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Started</span>
                      <span>{format(new Date(route.actualStartAt), "h:mm a")}</span>
                    </div>
                  )}
                  {route.actualEndAt && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Completed</span>
                      <span>{format(new Date(route.actualEndAt), "h:mm a")}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Completed Stops</span>
                    <span>
                      {route.stops.filter(s => ["COMPLETED", "DELIVERED", "COLLECTED"].includes(s.status)).length} / {route.stops.length}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Route?</AlertDialogTitle>
            <AlertDialogDescription>
              This will delete the route and remove all stop assignments. Shipments will need to be re-routed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? "Deleting..." : "Delete Route"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
